import { useCallback, useEffect, useRef } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { config } from "../config";
import {
  ConnectionStatus,
  updateConnectionStatus,
  updateError,
  updateLoading,
} from "../redux/slices/meta";
import { useAppDispatch } from "../redux/store";
import { GameMessage, PlayerAction, PlayerId, PlayerMessage } from "../types";
import { adaptWorkerMessage } from "../workerAdapter";

interface UseGameConnectionProps {
  playerId?: PlayerId;
  onMessage: (message: GameMessage) => void;
  onClose?: () => void;
  enabled: boolean;
}

export const useGameConnection = ({
  playerId,
  onMessage,
  onClose,
  enabled,
}: UseGameConnectionProps) => {
  const dispatch = useAppDispatch();
  const loadTimer = useRef<NodeJS.Timeout | undefined>(undefined);
  const isReconnecting = useRef(false);

  const socketUrl = playerId
    ? `${config.wsHost}/rooms?player_id=${playerId.id}&player_secret=${playerId.secret}&game_id=${playerId.game_id}`
    : null;

  const handleIncomingMessage = useCallback(
    (event: MessageEvent) => {
      dispatch(updateLoading(false));
      if (loadTimer.current) {
        clearTimeout(loadTimer.current);
        loadTimer.current = undefined;
      }

      try {
        let gameMessage = JSON.parse(event.data) as GameMessage;
        if (config.backend === "worker") {
          gameMessage = adaptWorkerMessage(gameMessage);
        }
        onMessage(gameMessage); // Pass the parsed message up to the logic layer
      } catch (err) {
        console.error("Error parsing WebSocket message:", err, event.data);
        dispatch(updateError("Received an invalid message from the server."));
      }
    },
    [dispatch, onMessage]
  );

  const { sendJsonMessage, readyState } = useWebSocket(
    socketUrl,
    {
      onOpen: () => {
        console.log("WebSocket connection established");
        isReconnecting.current = false;
      },
      onClose: (event) => {
        console.log(
          event.wasClean
            ? "WebSocket closed cleanly"
            : "WebSocket connection died"
        );
        if (!event.wasClean && enabled) {
          isReconnecting.current = true;
        }
        onClose?.();
      },
      onError: (event) => {
        console.error("WebSocket error:", event);
      },
      onReconnectStop: () => {
        console.warn("WebSocket: Failed to reconnect after multiple attempts.");
        dispatch(updateConnectionStatus(ConnectionStatus.CONNECTION_FAILED));
      },
      shouldReconnect: (closeEvent) => !closeEvent.wasClean && enabled,
      reconnectAttempts: 10,
      reconnectInterval: (attemptNumber) =>
        Math.min(1000 * 2 ** attemptNumber, 30000),
      retryOnError: true,
      onMessage: handleIncomingMessage,
    },
    enabled && !!socketUrl
  );

  useEffect(() => {
    let status: ConnectionStatus;
    switch (readyState) {
      case ReadyState.CONNECTING:
        status = isReconnecting.current
          ? ConnectionStatus.RECONNECTING
          : ConnectionStatus.CONNECTING;
        break;
      case ReadyState.OPEN:
        status = ConnectionStatus.CONNECTED;
        break;
      case ReadyState.CLOSING:
        status = ConnectionStatus.DISCONNECTED;
        break;
      case ReadyState.CLOSED:
        status = isReconnecting.current
          ? ConnectionStatus.CONNECTION_LOST
          : ConnectionStatus.DISCONNECTED;
        break;
      case ReadyState.UNINSTANTIATED:
      default:
        status = ConnectionStatus.DISCONNECTED;
    }
    dispatch(updateConnectionStatus(status));
  }, [readyState, dispatch]);

  const sendMessage = useCallback(
    (playerAction: PlayerAction, noLoading?: boolean) => {
      if (readyState !== ReadyState.OPEN || !playerId) {
        console.warn(
          "Cannot send message, WebSocket is not open or player ID is missing."
        );
        return;
      }

      const playerMessage: PlayerMessage = {
        game_id: playerId.game_id!,
        player_action: playerAction,
        player_id: playerId.id,
        player_secret: playerId.secret,
      };
      sendJsonMessage(playerMessage);

      if (!noLoading) {
        dispatch(updateLoading(true));
        if (loadTimer.current) clearTimeout(loadTimer.current);
        loadTimer.current = setTimeout(() => {
          dispatch(updateLoading(false));
          dispatch(
            updateError("Request timed out. Please check your connection.")
          );
        }, 5000);
      }
    },
    [readyState, sendJsonMessage, dispatch, playerId]
  );

  return {
    sendMessage,
    readyState,
  };
};
